/**
 * Debounce calls to the `fn` function by `ms` milliseconds.
 * Returned values are ignored.
 *
 * @param fn function to be debounced
 * @param ms debounce interval in milliseconds
 * @returns debounce wrapper around `fn`
 */
export const debounce = <T extends (...args: any[]) => any>(fn: T | undefined, ms: number) => {
    const manager: { handler?: ReturnType<typeof setTimeout> } = {};

    return (...args: Parameters<T>) => {
        clearTimeout(manager.handler!);
        manager.handler = setTimeout(() => fn?.(...args), ms);
    };
};

/**
 * Throttle calls to the `fn` function by `ms` milliseconds.
 * Returned values are ignored.
 *
 * @param fn function to be throttled
 * @param ms throttle interval in milliseconds
 * @returns throttle wrapper around `fn`
 */
export const throttle = <T extends (...args: any[]) => any>(fn: T | undefined, ms: number) => {
    const manager: { handler?: ReturnType<typeof setInterval>; args?: Parameters<T> } = {};

    return (...args: Parameters<T>): void => {
        if (manager.handler != undefined) return void (manager.args = args);
        fn?.(...args);
        manager.handler = setInterval(() => {
            if (manager.args) {
                fn?.(...manager.args);
                manager.args = undefined;
            } else {
                clearInterval(manager.handler!);
                manager.handler = undefined;
            }
        }, ms);
    };
};
